import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  getInitialDefrostSchedule,
  getInitialSequencer,
  getInitialSettings,
} from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form.helpers';
import { DetailsFormSchema } from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form.schema';
import { DetailsFormData } from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form.types';
import { useEquipmentDetailsRouteState } from '@/features/equipment-browser/equipment-details/hooks/use-equipment-details-route-state';
import { defrostScheduleValuesAtom } from '@/features/equipment-browser/equipment-details/state/defrost-schedule.state';
import { displaySettingsListAtom } from '@/features/equipment-browser/equipment-details/state/display-settings.state';
import { sequencesWithValuesAtom } from '@/features/equipment-browser/equipment-details/state/sequencer-editor.state';
import { activeSequencerDisplaySettingsListAtom } from '@/features/equipment-browser/equipment-details/state/sequencers.state';

/**
 * useDetailsForm
 *
 * Custom hook that manages the complete lifecycle of the equipment details form.
 * Handles form initialization, validation, dirty field tracking, and state synchronization.
 * Automatically resets form with new values when underlying data changes while preserving
 * user edits via keepDirtyValues option.
 *
 * @returns Object containing form controls and handlers
 * @returns form - React Hook Form instance with all form methods and state
 * @returns resetForm - Async function to reset form to initial or provided values
 */
export const useDetailsForm = () => {
  const displaySettings = useAtomValue(displaySettingsListAtom);
  const activeSequencerDisplaySettings = useAtomValue(activeSequencerDisplaySettingsListAtom);
  const sequencesWithValues = useAtomValue(sequencesWithValuesAtom);
  const defrostScheduleValues = useAtomValue(defrostScheduleValuesAtom);
  const { equipmentId } = useEquipmentDetailsRouteState();

  /**
   * Memoized initial form values computed from all equipment data sources.
   * Recalculates only when underlying data changes (settings, sequencers, or defrost schedule).
   * Combines all form sections into a single DetailsFormData object.
   */
  const initialValues = useMemo(() => {
    const initialSettings = getInitialSettings(displaySettings);
    const initialSequencerSettings = getInitialSettings(activeSequencerDisplaySettings);

    const sequencer = getInitialSequencer(sequencesWithValues);

    const defrostSchedule = getInitialDefrostSchedule(
      defrostScheduleValues?.defrostSchedule?.events ?? []
    );

    return {
      settings: initialSettings,
      sequencerSettings: initialSequencerSettings,
      sequencer: sequencer,
      defrostSchedule: defrostSchedule,
    } as DetailsFormData;
  }, [defrostScheduleValues, sequencesWithValues, displaySettings, activeSequencerDisplaySettings]);

  /**
   * React Hook Form instance configured with:
   * - Zod validation schema for type-safe form validation
   * - onChange mode for real-time validation feedback
   * - keepDirtyValues: true to preserve user edits when underlying data updates
   * - Automatic reset with new values when data sources change
   */
  const form = useForm({
    values: initialValues,
    mode: 'onChange',
    resolver: zodResolver(DetailsFormSchema),
    resetOptions: {
      keepDirtyValues: true, // keep dirty fields unchanged, but update defaultValues
    },
  });

  /**
   * Reset form completely when equipmentId changes.
   * Ensures a clean slate when switching between different equipment.
   */
  useEffect(() => {
    if (equipmentId !== undefined) {
      form.reset(initialValues, { keepDirtyValues: false });
    }
    // Intentionally omit `initialValues` from deps: only full reset when switching equipment.
    // Stream-driven updates sync via the `values` option on `useForm` without clearing dirty state.
  }, [equipmentId, form]);

  /**
   * Run schema validation whenever synced `initialValues` change so invalid server/stream data
   * surfaces in `formState.errors` / `isValid` without requiring a field interaction first.
   */
  useEffect(() => {
    void form.trigger();
  }, [initialValues, form]);

  /**
   * resetForm
   *
   * Resets the form to initial values or provided data, clearing all dirty state.
   * If data is provided, form resets to that data (used after successful submission).
   * If no data provided, resets to current initialValues from atoms.
   *
   * @param data - Optional form data to reset to (defaults to initialValues)
   * @returns Promise that resolves when reset is complete
   */
  const resetForm = async (data?: DetailsFormData): Promise<void> => {
    return form.reset(data || initialValues);
  };

  return { form, resetForm };
};
